<template>
  <hr class="p-0 w-100 m-0"/>
  <main class="home" ref="containerRef">
    <section class="debt-info">
      <!-- Total Debt Today Card -->
      <div class="info-card debt-card">
        <div class="title-container">
          <h3 class="card-title">{{ t("TOTAL_DEBT_TODAY") }}</h3>
        </div>
        <div class="card-amount total-debt">{{ displayAmount(props.payoffBalanceToday) }}</div>
        <p class="card-description">
          {{ t("TOTAL_DEBT_TODAY_DESCRIPTION") }}
        </p>
      </div>

      <!-- Minimum Payment Card -->
      <div class="info-card payment-card">
        <div class="title-container">
          <h3 class="card-title">{{ t("MINIMUM_WEEKLY_PAYMENT") }}</h3>
        </div>
        <div class="card-amount minimum-payment">{{ displayAmount(props.minimumPaymentAmount) }}</div>
        <p class="card-description">
          {{ t("MINIMUM_PAYMENT_DESCRIPTION") }}
          <span class="highlight-amount">{{ displayAmount(totalFinalCost) }}</span>
        </p>
        <div class="card-note">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
                d="M7.08333 10.625C7.28403 10.625 7.45226 10.5571 7.58802 10.4214C7.72379 10.2856 7.79167 10.1174 7.79167 9.91667C7.79167 9.71597 7.72379 9.54774 7.58802 9.41198C7.45226 9.27622 7.28403 9.20833 7.08333 9.20833C6.88264 9.20833 6.71441 9.27622 6.57865 9.41198C6.44288 9.54774 6.375 9.71597 6.375 9.91667C6.375 10.1174 6.44288 10.2856 6.57865 10.4214C6.71441 10.5571 6.88264 10.625 7.08333 10.625ZM6.375 7.79167H7.79167V3.54167H6.375V7.79167ZM7.08333 14.1667C6.10347 14.1667 5.18264 13.9807 4.32083 13.6089C3.45903 13.237 2.70938 12.7323 2.07188 12.0948C1.43438 11.4573 0.929688 10.7076 0.557813 9.84583C0.185938 8.98403 0 8.0632 0 7.08333C0 6.10347 0.185938 5.18264 0.557813 4.32083C0.929688 3.45903 1.43438 2.70938 2.07188 2.07188C2.70938 1.43438 3.45903 0.929688 4.32083 0.557813C5.18264 0.185938 6.10347 0 7.08333 0C8.0632 0 8.98403 0.185938 9.84583 0.557813C10.7076 0.929688 11.4573 1.43438 12.0948 2.07188C12.7323 2.70938 13.237 3.45903 13.6089 4.32083C13.9807 5.18264 14.1667 6.10347 14.1667 7.08333C14.1667 8.0632 13.9807 8.98403 13.6089 9.84583C13.237 10.7076 12.7323 11.4573 12.0948 12.0948C11.4573 12.7323 10.7076 13.237 9.84583 13.6089C8.98403 13.9807 8.0632 14.1667 7.08333 14.1667ZM7.08333 12.75C8.66528 12.75 10.0052 12.201 11.1031 11.1031C12.201 10.0052 12.75 8.66528 12.75 7.08333C12.75 5.50139 12.201 4.16146 11.1031 3.06354C10.0052 1.96563 8.66528 1.41667 7.08333 1.41667C5.50139 1.41667 4.16146 1.96563 3.06354 3.06354C1.96563 4.16146 1.41667 5.50139 1.41667 7.08333C1.41667 8.66528 1.96563 10.0052 3.06354 11.1031C4.16146 12.201 5.50139 12.75 7.08333 12.75Z"
                fill="#6B6161"/>
          </svg>
          <span>{{ t("CARD_CHARGES_NOTE") }}</span>
        </div>
      </div>
    </section>

    <!-- Upcoming Payments Section -->
    <div class="main-body">
      <h2 class="section-title" id="title">
        {{ t("UPCOMING_PAYMENTS") }}
      </h2>

      <div
          v-if="!props.paymentSchedule || props.paymentSchedule?.length === 0"
          class="mx-3 mt-2"
      >
        <h2 id="no-payments-title">
          {{ t("CL_NO_PAYMENTS_DUE") }}
        </h2>
      </div>
      <div v-else>
        <div
            class="container w-100 d-flex justify-content-center flex-column pt-0 px-0"
        >
          <ListItem
              v-for="(payment, index) in nextTwoPayments"
              v-bind:key="index"
              :icon="index === 0 ? dueIconPath : pendingIconPath"
              :name="displayTransactionDate(payment.dueDate)"
              @click="onItemClick(payment, index)"
          >
            <div class="due-badge" v-if="index === 0">
              {{ t("DUE") }}
            </div>
            <div class="due-badge" v-if="index === 1">
              {{ t("NEXT") }}
            </div>
          </ListItem>
        </div>
      </div>
    </div>

    <div class="payment-button-container">
      <Button @click="redirectToPayment" type="rounded">
        {{ t("PAY_NOW") }}
      </Button>
    </div>
  </main>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, ref} from "vue";
import {useI18n} from "@/apps/credit-line/composables/useI18n";
import {IPaymentSchedule} from "@/apps/credit-line/models/CreditLineDetails";
import ListItem from "@/apps/credit-line/components/Payments/ListItem.vue";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import Button from "@/apps/credit-line/components/Button.vue";
import {AuthenticationHelper} from "@/apps/credit-line/helpers/authentication.helper";
import {resizeComponentHelper} from "@/apps/credit-line/helpers/resizeComponent.helper";

// Props
const props = defineProps<{
  paymentSchedule: IPaymentSchedule[];
  payoffBalanceToday: number;
  minimumPaymentAmount: number;
}>();

const dueIconPath = "img/credit-line/pending_icon.svg";
const pendingIconPath = "img/credit-line/pending.svg";
const {t, locale} = useI18n();
const containerRef = ref();

const emit = defineEmits(["onItemClick"]);

const setDuePayment = inject<(isDue: boolean) => void>("setDuePayment");

const totalFinalCost = computed(() => {
  const schedule = props.paymentSchedule || [];
  return schedule.reduce((sum, payment) => {
    return sum + Number(payment.minimumPaymentAmount || 0);
  }, 0);
});

const nextTwoPayments = computed(() => {
  return (props.paymentSchedule || []).slice(0, 2);
});

const onItemClick = (payment: IPaymentSchedule, index: number) => {
  setDuePayment?.(index === 0);
  emit("onItemClick", payment);
};

const displayAmount = (val: number): string => {
  return currencyFormat(val);
};

const displayTransactionDate = (val: number): string => {
  const date = new Date(val * 1000);
  const month = date.toLocaleDateString(locale.value.replace("_", "-"), {
    month: "short",
  });
  const day = date.getDate();

  return `${month.replace(/\b\w/g, (char) => char.toUpperCase())} ${day}`;
};

const redirectToPayment = (): void => {
  const deviceTag = AuthenticationHelper.getDeviceTag();
  window.location.href = `https://pay.payjoy.com/p/device/${deviceTag}`;
};

onMounted(() => {
  resizeComponentHelper(containerRef);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.home {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 2rem;
}

.title-container {
  height: 1.5rem;
}

.debt-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-left: 1rem;
  margin-right: 1rem;
  padding-top: 1.5rem;
  margin-bottom: 0;

  & p {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }
}

.info-card {
  background: white;
  background: var(--Surface-Surface-Bright, #FFF);
  box-shadow: 0 1px 10.5px 0 rgba(169, 169, 169, 0.15);
  padding: 1.25rem;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

.debt-card {
  border-radius: 1.5rem 1.5rem 0 0;
  padding-bottom: 0 !important;
}

.payment-card {
  border-top: 1px solid var(--Extras-Outline-Variant, #CFC9C9);
  margin-top: -15px;
  border-radius: 0 0 1.5rem 1.5rem;
  padding-bottom: 0;
  padding-top: 0;

  & .title-container {
    margin-top: 16px !important;
  }

  & .card-note {
    margin-bottom: 1.5rem;
  }
}

.card-title {
  color: var(--Surface-On-Surface, #1A1919);

  /* PJ/baseline/body/small */
  font-family: var(--static-body-small-font, Inter);
  font-size: var(--static-body-small-size, 1rem);
  font-style: normal;
  font-weight: 400;
  line-height: var(--static-body-small-line-height, 1.5rem); /* 150% */
  letter-spacing: var(--letter-spacing-narrow, -0.03125rem);
}

.card-amount {
  font-size: 2rem;
  font-weight: 700;
  margin: 0.5rem 0;
  font-family: Nunito, sans-serif;

  &.total-debt {
    margin: 0;
    color: var(--tertiary-extra-orange-50, #FF9500);
    font-family: var(--static-display-small-font, Inter);
    font-size: var(--static-display-small-size, 2rem);
    font-style: normal;
    font-weight: 600;
    line-height: var(--static-display-small-line-height, 2.75rem); /* 137.5% */
    letter-spacing: -0.03125rem;
  }

  &.minimum-payment {
    margin: 0;
    color: var(--Primary-Primary, #007C3B);
    font-family: var(--static-display-small-font, Inter);
    font-size: var(--static-display-small-size, 2rem);
    font-style: normal;
    font-weight: 600;
    line-height: var(--static-display-small-line-height, 2.75rem); /* 137.5% */
    letter-spacing: -0.03125rem;
  }
}

.card-description {
  color: var(--Surface-On-Surface-Variant, #6B6161);

  /* PJ/baseline/body/small */
  font-family: var(--static-body-small-font, Inter);
  font-size: var(--static-body-small-size, 1rem);
  font-style: normal;
  font-weight: 400;
  line-height: var(--static-body-small-line-height, 1.5rem); /* 150% */
  letter-spacing: var(--letter-spacing-narrow, -0.03125rem);
}

.highlight-amount {
  color: var(--tertiary-extra-orange-50, #FF9500);

  /* PJ/baseline/body/small */
  font-family: var(--static-body-small-font, Inter);
  font-size: var(--static-body-small-size, 1rem);
  font-style: normal;
  font-weight: 400;
  line-height: var(--static-body-small-line-height, 1.5rem);
  letter-spacing: var(--letter-spacing-narrow, -0.03125rem);
}

.card-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0;
  background-color: transparent;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #666;
  line-height: 1.4;
  margin-top: 1rem;

  & svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
  }

  span {
    color: var(--Surface-On-Surface-Variant, #6B6161);

    /* PJ/baseline/legal/small */
    font-family: var(--static-body-small-font, Inter);
    font-size: var(--static-legal-small-size, 12px);
    font-style: normal;
    font-weight: 400;
    line-height: var(--static-legal-small-line-height, 16px); /* 133.333% */
    letter-spacing: var(--letter-spacing-narrow, -0.5px);
  }
}

.due-badge {
  color: var(--Surface-Inverse-Surface, #343232);

  /* PJ/baseline/legal/small */
  font-family: var(--static-body-small-font, Inter);
  font-size: var(--static-legal-small-size, 12px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--static-legal-small-line-height, 16px); /* 133.333% */
  letter-spacing: var(--letter-spacing-narrow, -0.5px);
}

.main-body {
  margin-top: -1rem;
  padding: 0 1rem;
}

.section-title {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #000;
  margin: 1.5rem 0 1rem 0;
  padding: 0;
  font-family: Nunito, sans-serif;
}

#no-payments-title {
  background-color: $pj-color-light-silver;
  font-size: medium;
  border-radius: 11px;
  padding: 2rem;
}

.stripe-div {
  height: 50px;
  align-items: center;
  background-color: $pj-color-green-hover;
}

.bottom-buttons {
  width: 100%;
  margin: 0 auto;
  position: fixed;
  bottom: 0;
}

.buttons-container {
  display: -webkit-box;
  padding-left: 3.5%;
}

.payment-button-container {
  position: fixed;
  bottom: 2rem;
  left: 0;
  right: 0;
  width: 100%;
  padding: 0 1rem;
  z-index: 10;
}

#title {
  color: var(--Surface-On-Surface, #1A1919);
  font-family: var(--static-title-medium-font, Inter);
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.375rem; /* 137.5% */
  letter-spacing: -0.03125rem;
}

.Button {
  border-radius: 6.25rem;
}
</style>
